import { useEffect, useRef } from "react";

/**
 * Inline Calendly scheduler. Lazy-loads the Calendly widget JS once per
 * SPA session (shared with CalendlyBadge — both use the same script tag),
 * then mounts the inline embed inside our own ref'd div.
 *
 * Renders a sized container even before the widget JS resolves so the
 * page doesn't jump when the embed pops in.
 */
const JS_SRC = "https://assets.calendly.com/assets/external/widget.js";

function loadCalendlyScript() {
    if (typeof document === "undefined") return Promise.resolve();
    return new Promise((resolve) => {
        if (window.Calendly) {
            resolve();
            return;
        }
        let script = document.querySelector(`script[data-calendly-loader="true"]`);
        if (!script) {
            script = document.createElement("script");
            script.src = JS_SRC;
            script.async = true;
            script.dataset.calendlyLoader = "true";
            document.body.appendChild(script);
        }
        const check = () => (window.Calendly ? resolve() : setTimeout(check, 50));
        check();
    });
}

export default function CalendlyInlineEmbed({
    url = "https://calendly.com/ashlee-asvmedicalservices",
    backgroundColor = "f9f9f9",
    primaryColor = "72a597",
    minHeight = 700,
    testid = "calendly-inline-embed",
}) {
    const containerRef = useRef(null);

    useEffect(() => {
        let cancelled = false;
        loadCalendlyScript().then(() => {
            if (cancelled || !containerRef.current) return;
            if (!window.Calendly?.initInlineWidget) return;
            // Wipe any prior instance inside the container so a remount
            // (e.g. country/lang change above) doesn't double-embed.
            containerRef.current.innerHTML = "";
            try {
                window.Calendly.initInlineWidget({
                    url: `${url}?background_color=${backgroundColor}&primary_color=${primaryColor}`,
                    parentElement: containerRef.current,
                });
            } catch (_e) { /* embed will silently no-op — Calendly will retry on next render */ }
        });
        return () => { cancelled = true; };
    }, [url, backgroundColor, primaryColor]);

    return (
        <div
            ref={containerRef}
            data-testid={testid}
            className="calendly-inline-widget rounded-3xl overflow-hidden bg-[#f9f9f9]"
            style={{ minWidth: 320, height: minHeight }}
        />
    );
}
